import { useMutation, useQuery } from '@tanstack/react-query'
import { ArgProps, GeneralResMock } from '@type/general.type'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'
import { DispenceInc, DispenseSchema } from '../types/dispense.type'

type Props = {
  dispensary_type?: 'inpatient_DAP' | 'outpatient_DAP' | 'udds'
  status?: string
} & ArgProps
export const useDispensing = (arg?: Props) => {
  const { dispensary_type, id, limit, page, query, status } = arg ?? {}
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'
  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<DispenseSchema[], DispenceInc>>('dispensings', {
        params: {
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(sort_by && { sort_by }),
          ...(sort_dir && { sort_dir }),
          ...(query && { query }),
          ...(dispensary_type && { dispensary_type }),
          ...(status && { status })
        }
      })
      return data
    },
    queryKey: ['dispensing', { dispensary_type, limit, page, query, sort_by, sort_dir, status }]
  })

  const { data: singleDispense, isLoading: dispenseIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<DispenseSchema, DispenceInc>>(`dispensings/${id}`)
      return data
    },
    queryKey: ['dispensing', { id }]
  })

  const { mutateAsync: dispense } = useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await AXIOS_INSTANCE.post('dispensings', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispensing'] })
      queryClient.invalidateQueries({ queryKey: ['medication'] })
      queryClient.invalidateQueries({ queryKey: ['product_inventories'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })

  const { mutateAsync: updateDispense } = useMutation({
    mutationFn: async ({ payload, id }: { payload: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`dispensings/${id}`, payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['dispensing'] })
      queryClient.invalidateQueries({ queryKey: ['dispensing', { id }] })
      queryClient.invalidateQueries({ queryKey: ['medication'] })
      queryClient.invalidateQueries({ queryKey: ['product_inventories'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })
  return {
    data: data?.data,
    dispense,
    dispenseIsLoading,
    include: data?.included,
    isLoading,
    pagination: data?.meta.pagination,
    singleDispense,
    updateDispense
  }
}
